import { useState } from "react";

function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  // Chinese/CJK names: the family name character carries the identity.
  if (/^[一-鿿]/.test(trimmed)) return trimmed.slice(0, 1);
  // Latin names: initials of up to the first two words.
  return trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

interface SmartAvatarProps {
  src?: string;
  name: string;
  className?: string;
}

/**
 * Drop-in replacement for a team-member `<img>` headshot. Falls back to a
 * navy/silver initials badge — generated from the person's own name — when
 * no photo is set yet or the photo fails to load, instead of a broken image.
 */
export default function SmartAvatar({ src, name, className = "" }: SmartAvatarProps) {
  const [failed, setFailed] = useState(!src);

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 ${className}`}
        role="img"
        aria-label={name}
      >
        <span className="font-serif font-semibold text-white/70 text-3xl md:text-4xl select-none">
          {getInitials(name)}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}
